/**
 * assets/navigation.js — Studio 1/1
 * ---------------------------------------------------------------------------
 * Mobile nav overlay open/close for sections/site-header.liquid.
 *
 * Responsibilities:
 *   - Open/close the full-panel mobile nav (#MobileNav) with scroll lock
 *   - Focus trap inside the open panel (Tab / Shift+Tab cycling)
 *   - Escape key closes the panel
 *   - Sync aria-expanded on the hamburger trigger
 *
 * Consumed data-* hooks (set in sections/site-header.liquid):
 *   [data-nav-toggle]   Hamburger button
 *   [data-nav-close]    Any link/element inside #MobileNav that should close it
 *   [data-mobile-nav]   Mobile nav panel root (#MobileNav)
 * ---------------------------------------------------------------------------
 */

'use strict';

(() => {
  const trigger = document.querySelector('[data-nav-toggle]');
  const menu = document.querySelector('[data-mobile-nav]');

  if (!trigger || !menu) return;

  const FOCUSABLE_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  const supportsInert = 'inert' in HTMLElement.prototype;
  let menuOpen = false;
  let previousFocus = null;

  function getPageContent() {
    return [document.querySelector('#MainContent'), document.querySelector('.site-footer')].filter(Boolean);
  }

  function getFocusableEls() {
    return Array.from(menu.querySelectorAll(FOCUSABLE_SELECTORS));
  }

  function lockScroll() {
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.dataset.scrollY = scrollY;
  }

  function unlockScroll() {
    const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    delete document.body.dataset.scrollY;
    window.scrollTo(0, scrollY);
  }

  function openMenu() {
    if (menuOpen) return;
    menuOpen = true;
    previousFocus = document.activeElement;

    menu.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    lockScroll();

    if (supportsInert) getPageContent().forEach((el) => { el.inert = true; });

    getFocusableEls()[0]?.focus();
  }

  function closeMenu() {
    if (!menuOpen) return;
    menuOpen = false;

    menu.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
    unlockScroll();

    if (supportsInert) getPageContent().forEach((el) => { el.inert = false; });

    previousFocus?.focus();
  }

  trigger.addEventListener('click', () => (menuOpen ? closeMenu() : openMenu()));

  menu.querySelectorAll('[data-nav-close]').forEach((el) => {
    el.addEventListener('click', closeMenu);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && menuOpen) {
      closeMenu();
      return;
    }

    if (e.key === 'Tab' && menuOpen) {
      const focusable = getFocusableEls();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  trigger.setAttribute('aria-expanded', 'false');
})();
